import { useState } from "react";
import { DEFAULT_SORT, nextSort, sortLabel } from "../ui/feedSorting.js";
import { ProviderAvatar } from "./ProjectHeader.jsx";
import { percentileOrdinal, percentileTint, percentileTone } from "../ui/percentileTone.js";

/**
 * ProjectFeedList
 *
 * One page of a project's calendars. The state that decides *which* rows are on
 * screen — the search, the ordering, the page — lives in the screen above and
 * arrives as props, because every one of them changes the query: this component
 * renders a page, it does not decide which page to fetch.
 *
 * Props:
 *   project — { name, feedCount, mode, pollingSummary }
 *   feeds — [{ id, feedId, externalId, url, lastChangeLabel, percentile, unreachable }]
 *   totalFeedCount — every matching row, not just this page
 *   page, pageSize — the page being shown, 1-based
 *   sort — { key, order }; `created` is the listing's default
 *   onAddFeed(url, externalId)
 *   onOpenFeed(feedId) — the calendar's id, not this project's channel id
 *   onRemoveFeed(channel) — resolves with the server's cleanup report
 *   onSearch(query)
 *   onSortChange(next) — { key, order }
 *   onPageChange(page)
 *   onPageSizeChange(size)
 */

/** PAGE_SIZES are the page sizes a customer can choose between. */
export const PAGE_SIZES = [25, 50, 100, 250, 500];

/**
 * SHORT_ID_LENGTH is how much of an internal id a row shows.
 *
 * The id is there to be recognised and copied — into a support thread, a log, a
 * search box — not to be read digit by digit, and a full UUID in a table cell
 * either wraps or forces every other column to shrink. Eight hex characters are
 * enough to recognise and short enough to read; the whole value is in the title
 * and in the search, which matches on the full text.
 */
const SHORT_ID_LENGTH = 8;

/**
 * urlTail is the part of a feed URL after its host.
 *
 * "https://login.smoobu.com/ical/3248402.ics?s=…" becomes
 * "/ical/3248402.ics?s=…". The scheme and host are what a provider's own mark
 * already says; the path is what distinguishes one calendar from another on the
 * same service. A URL that does not parse is returned whole, because a guess at
 * where the host ends would cut an address that is not shaped like one.
 */
export function urlTail(url) {
  const match = /^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i.exec(url ?? "");
  return match ? match[1] || "/" : url ?? "";
}

export default function ProjectFeedList({
  project,
  feeds = [],
  totalFeedCount = 0,
  page = 1,
  pageSize = 50,
  sort = DEFAULT_SORT,
  providerFilter = "",
  providerOptions = [],
  onProviderChange,
  onClearProvider,
  onAddFeed,
  onOpenFeed,
  onRemoveFeed,
  onSearch,
  onSortChange,
  onPageChange,
  onPageSizeChange,
}) {
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newExternalId, setNewExternalId] = useState("");
  // Deletion is confirmed in the row rather than through window.confirm: the
  // question has to explain what happens to the calendar behind the channel, and
  // a browser dialog cannot be styled, tested or read by the page.
  const [confirming, setConfirming] = useState(null);
  const [removing, setRemoving] = useState(false);

  function handleSearch(e) {
    const v = e.target.value;
    setQuery(v);
    onSearch?.(v);
  }

  function handleAddSubmit(e) {
    e.preventDefault();
    onAddFeed?.(newUrl, newExternalId);
    setNewUrl("");
    setNewExternalId("");
    setShowAdd(false);
  }

  const total = Number(totalFeedCount) || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total, (page - 1) * pageSize + feeds.length);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-lg)" }}>{project?.name}</p>
          <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
            {project?.feedCount?.toLocaleString()} feeds · {project?.mode} · {project?.pollingSummary}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="ical-input"
            type="search"
            placeholder="Search URL, ext. or feed ID"
            style={{ width: 240 }}
            value={query}
            onChange={handleSearch}
          />
          {/*
            The providers of THIS project, most-held first, plus the overflow
            group. The group is listed but not selectable: it folds unrelated
            providers together, so there is no hostname to filter by and an
            option that silently filtered nothing would be worse than an option
            that says why it cannot.
          */}
          {providerOptions.length > 0 && (
            <select
              className="ical-select"
              aria-label="Filter by provider"
              value={providerFilter}
              onChange={(event) => onProviderChange?.(event.target.value)}
            >
              <option value="">All providers</option>
              {providerOptions.map((option) => (
                <option key={option.provider} value={option.provider} disabled={!option.filterable}>
                  {option.filterable ? option.provider : `${option.provider} (not filterable)`}
                </option>
              ))}
            </select>
          )}
          <button className="ical-btn ical-btn-primary" onClick={() => setShowAdd((s) => !s)}>+ Add iCal</button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={handleAddSubmit} className="ical-card" style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Feed URL</label>
            <input className="ical-input" type="url" required style={{ width: "100%" }} value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://... or webcal://..." />
          </div>
          <div style={{ width: 160 }}>
            <label style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>External ID</label>
            <input className="ical-input" style={{ width: "100%" }} value={newExternalId} onChange={(e) => setNewExternalId(e.target.value)} placeholder="villa-42" />
          </div>
          <button type="submit" className="ical-btn ical-btn-primary">Add</button>
        </form>
      )}

      {/*
        Anything narrowing the list is shown here, with its own way out. Without
        it a search or a provider filter is a state the customer can see the
        effect of but not the cause, and the only way back to "everything" is a
        reload — which is how a filter turns into a trap.
      */}
      <ActiveFilters
        search={query}
        provider={providerFilter}
        sort={sort}
        onSearch={onSearch}
        onClearProvider={onClearProvider}
        onSortChange={onSortChange}
      />

      <div className="ical-feed-head" style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
        <span>Ext. ID</span>
        <span>Feed ID</span>
        {/*
          The provider is a mark rather than a name: the column is there to be
          scanned down, and a name would either truncate ("login.smoobu.com") or
          steal the width the URL needs. The whole name is in the title.
        */}
        <span title="Calendar provider">Prov.</span>
        <span>URL</span>
        <SortHeader label="Last change" sortKey="last_change" sort={sort} onSortChange={onSortChange} />
        <SortHeader label="Percentile" sortKey="percentile" sort={sort} onSortChange={onSortChange} />
        <span />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {feeds.map((f) => {
          const isConfirming = confirming === f.id;
          const id = f.feedId ?? f.id;
          return (
            <div
              key={f.id}
              className="ical-row ical-feed-row"
              style={{ cursor: isConfirming ? "default" : "pointer", opacity: f.unreachable ? 0.6 : 1 }}
              onClick={isConfirming ? undefined : () => onOpenFeed?.(f.feedId ?? f.id)}
            >
              {isConfirming ? (
                <>
                  {/*
                    The honest answer to "what does removing this do?" depends on
                    whether another project still uses the same calendar, which
                    only the server knows — so the copy states both outcomes
                    instead of promising one.
                  */}
                  <span style={{ gridColumn: "1 / -1", fontSize: "var(--text-sm)" }}>
                    Remove <strong>{f.externalId}</strong> from this project? If no other project
                    subscribes to this calendar, its record, cached events and request log are
                    removed too.
                  </span>
                  <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="ical-btn ical-btn-danger"
                      disabled={removing}
                      onClick={async (event) => {
                        event.stopPropagation();
                        setRemoving(true);
                        try {
                          // A handler that reports failure keeps the question
                          // open, so the row does not look as though it went
                          // away while the server still has it.
                          const removed = await onRemoveFeed?.(f);
                          if (removed !== false) setConfirming(null);
                        } finally {
                          setRemoving(false);
                        }
                      }}
                    >
                      {removing ? "Removing…" : "Remove"}
                    </button>
                    <button
                      type="button"
                      className="ical-btn"
                      disabled={removing}
                      onClick={(event) => {
                        event.stopPropagation();
                        setConfirming(null);
                      }}
                    >
                      Cancel
                    </button>
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: "var(--text-sm)", fontFamily: "var(--font-mono)" }}>{f.externalId}</span>
                  {/* Truncated, with the whole value in the title: the id is what a
                      customer pastes into a search or a support thread, and the
                      search matches on all of it, not on the eight characters shown. */}
                  <span
                    style={{ fontSize: "var(--text-xs)", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}
                    title={id ? String(id) : undefined}
                  >
                    {id ? String(id).slice(0, SHORT_ID_LENGTH) : "—"}
                  </span>
                  <span style={{ display: "flex" }} title={f.provider || "no provider named"}>
                    {/*
                      Empty rather than a dash when the address named no service:
                      the percentile column already owns the em dash for "no
                      rank", and two of them in one row read as the same
                      statement twice.
                    */}
                    {f.provider ? <ProviderAvatar name={f.provider} size={24} /> : null}
                  </span>
                  <span
                    className="ical-feed-url"
                    style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={f.url}
                  >
                    {/*
                      Two renderings of one address, and the stylesheet shows one
                      of them. The scheme and the hostname are the longest part of
                      a feed URL and the least of what identifies it — the
                      provider's mark already names the service — so a narrow row
                      shows the path and everything after it, which is the part
                      that tells two feeds apart. Duplicated text rather than a
                      width measurement, because a browser that renders the wrong
                      one would be showing the wrong address in the interval
                      before the measurement landed.
                    */}
                    <span className="ical-feed-url-full">{f.url}</span>
                    <span className="ical-feed-url-tail">{urlTail(f.url)}</span>
                  </span>
                  {f.unreachable ? (
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--text-danger)" }}>unreachable</span>
                  ) : (
                    <span style={{ fontSize: "var(--text-sm)" }}>{f.lastChangeLabel}</span>
                  )}
                  <PercentileBadge percentile={f.percentile} />
                  <span className="ical-feed-actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                    <button
                      type="button"
                      className="ical-btn"
                      style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
                      aria-label={`Remove ${f.externalId}`}
                      onClick={(event) => {
                        // The row itself opens the feed; removing is a different
                        // intention and must not also navigate.
                        event.stopPropagation();
                        setConfirming(f.id);
                      }}
                    >
                      Remove
                    </button>
                    <span style={{ color: "var(--text-muted)" }}>›</span>
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
        {/* The count is what tells a customer the rows they cannot see exist. */}
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          {total === 0 ? "No feeds match" : `Showing ${firstRow}–${lastRow} of ${total.toLocaleString()}`}
          {sortLabel(sort) ? ` · sorted by ${sortLabel(sort)}` : ""}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {sortLabel(sort) ? (
            <button
              type="button"
              className="ical-btn"
              style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
              onClick={() => onSortChange?.(DEFAULT_SORT)}
            >
              Default order
            </button>
          ) : null}

          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            Per page
            <select
              className="ical-select"
              style={{ padding: "4px 8px", fontSize: "var(--text-xs)" }}
              value={pageSize}
              onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
              aria-label="Feeds per page"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              className="ical-btn"
              style={{ padding: "2px 10px", fontSize: "var(--text-xs)" }}
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              className="ical-btn"
              style={{ padding: "2px 10px", fontSize: "var(--text-xs)" }}
              disabled={page >= pageCount}
              onClick={() => onPageChange?.(page + 1)}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SortHeader is a column heading that orders the list.
 *
 * It is a button, not a click handler on the heading text: sorting has to be
 * reachable from the keyboard and announced to a screen reader, and `aria-sort`
 * on the header is what says which column is currently deciding the order.
 */
/**
 * ActiveFilters lists every criterion that is narrowing the page, each with a
 * control that removes it.
 *
 * The chips are derived from the same state the query is built from, so a chip
 * cannot describe a filter the list is not applying — the failure this exists to
 * prevent is a customer who searched, sees fewer rows, and has no way back
 * except reloading the page.
 */
function ActiveFilters({ search, provider, sort, onSearch, onClearProvider, onSortChange }) {
  const chips = [];

  if (search) {
    chips.push({
      key: "search",
      label: `Search: “${search}”`,
      onClear: () => onSearch?.(""),
    });
  }
  if (provider) {
    chips.push({
      key: "provider",
      label: `Provider: ${provider}`,
      onClear: () => onClearProvider?.(),
    });
  }
  if (sortLabel(sort)) {
    chips.push({
      key: "sort",
      label: `Sorted by ${sortLabel(sort)}`,
      onClear: () => onSortChange?.(DEFAULT_SORT),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="ical-badge ical-badge-neutral"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px" }}
        >
          {chip.label}
          <button
            type="button"
            aria-label={`Remove filter: ${chip.label}`}
            onClick={chip.onClear}
            style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, fontSize: "var(--text-sm)", lineHeight: 1 }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

function SortHeader({ label, sortKey, sort, onSortChange }) {
  const active = sort?.key === sortKey;
  const ariaSort = active ? (sort.order === "asc" ? "ascending" : "descending") : "none";
  return (
    <span role="columnheader" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSortChange?.(nextSort(sort, sortKey))}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          cursor: "pointer",
          color: active ? "var(--text-primary)" : "inherit",
          fontWeight: active ? 600 : "inherit",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {label}
        <span aria-hidden="true" style={{ fontSize: 10 }}>{active ? (sort.order === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </span>
  );
}

/**
 * PercentileBadge prints a feed's change percentile, coloured by the ramp that
 * runs from grey through a dull yellow to green — the top of the scale being the
 * calendars that change most, which is what the ranking is for.
 *
 * `null` is not zero: a feed with no change in the window has no rank at all, and
 * showing it as "0th" would put it at the bottom of a comparison it is not in.
 */
function PercentileBadge({ percentile }) {
  if (percentile == null) {
    return (
      <span
        className="ical-badge ical-badge-neutral"
        // Marked so a narrow screen can drop it. The explanation lives in the
        // title, and a phone has no hover: a small neutral pill holding an em
        // dash reads as a button whose purpose nobody can discover.
        data-empty="true"
        title="No change detected in this calendar over the last 28 days, so it has no rank yet."
      >
        —
      </span>
    );
  }
  return (
    <span
      className="ical-badge"
      data-percentile={percentile}
      title={`This calendar changed more often than ${percentile}% of the calendars we track (last 28 days).`}
      style={{ background: percentileTint(percentile), color: percentileTone(percentile), fontWeight: 500 }}
    >
      {percentileOrdinal(percentile)}
    </span>
  );
}
